import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Check, Clock3, CreditCard, MapPin, Search, ShieldCheck, ShoppingBag, ShoppingCart, Sparkles, Store, Truck, X } from 'lucide-react';

const MOCK_STORES = [
  {
    id: 'divorare',
    name: 'Divorare',
    category: 'Comida rápida',
    eta: '25-35 min',
    deliveryFee: 1.5,
    accent: 'from-fuchsia-500/30 via-violet-500/20 to-cyan-400/20',
    products: [
      {
        id: 'burger-clasica',
        name: 'Hamburguesa clásica',
        description: 'Pan brioche, carne jugosa, queso cheddar y salsa especial.',
        price: 5,
        image: '🍔',
        modifiers: ['Papas +1.00', 'Refresco +0.80', 'Extra queso +0.60']
      },
      {
        id: 'combo-pizza',
        name: 'Combo pizza personal',
        description: 'Pizza individual con bebida fría incluida.',
        price: 8,
        image: '🍕',
        modifiers: ['Borde de queso +1.20', 'Salsa extra +0.40']
      }
    ]
  },
  {
    id: 'fresh-market',
    name: 'Fresh Market',
    category: 'Abastos y víveres',
    eta: '30-45 min',
    deliveryFee: 2,
    accent: 'from-emerald-500/25 via-teal-500/15 to-cyan-400/15',
    products: [
      {
        id: 'frutas-box',
        name: 'Box de frutas',
        description: 'Selección fresca para la semana.',
        price: 6.5,
        image: '🍎',
        modifiers: ['Cambiar banana por uvas', 'Agregar granola +0.90']
      }
    ]
  }
];

const PAYMENT_METHODS = [
  { id: 'fires', label: 'Fires', icon: '🔥', note: 'Pago rápido dentro de MundoXYZ' },
  { id: 'bs', label: 'Bolívares', icon: '💸', note: 'Transferencia o pago móvil' },
  { id: 'usdt', label: 'USDT', icon: '₮', note: 'Ideal para pagos en dólares digitales' }
];

const formatUsd = (value) => `$${Number(value || 0).toFixed(2)}`;

const SimulatedMarketPurchaseFlow = ({ onComplete, onPhaseChange }) => {
  const [phase, setPhase] = useState('browse');
  const [search, setSearch] = useState('');
  const [selectedStore, setSelectedStore] = useState(MOCK_STORES[0]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedModifier, setSelectedModifier] = useState('Papas +1.00');
  const [quantity, setQuantity] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState('Residencias MundoXYZ, Torre 2, apto 5B');
  const [deliveryReference, setDeliveryReference] = useState('Portón azul, llamar al llegar');
  const [paymentMethod, setPaymentMethod] = useState('fires');
  const timersRef = useRef([]);

  useEffect(() => {
    if (typeof onPhaseChange === 'function') {
      onPhaseChange(phase);
    }
  }, [phase, onPhaseChange]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (phase !== 'success') return;
    const timer = setTimeout(() => {
      if (typeof onComplete === 'function') onComplete();
    }, 2200);
    timersRef.current.push(timer);
  }, [phase, onComplete]);

  const visibleStores = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return MOCK_STORES;
    return MOCK_STORES.filter((store) => {
      const inStore = store.name.toLowerCase().includes(query) || store.category.toLowerCase().includes(query);
      const inProducts = store.products.some((product) => product.name.toLowerCase().includes(query));
      return inStore || inProducts;
    });
  }, [search]);

  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + item.price * item.quantity + item.modifierPrice * item.quantity, 0);
  }, [cartItems]);

  const deliveryFee = selectedStore?.deliveryFee || 0;
  const total = subtotal + deliveryFee;

  const openProduct = (store, product) => {
    setSelectedStore(store);
    setSelectedProduct(product);
    setSelectedModifier(product.modifiers[0] || 'Sin cambios');
    setQuantity(1);
    setPhase('product');
  };

  const addCurrentProductToCart = () => {
    if (!selectedProduct) return;
    const modifierPriceMatch = /\+(\d+(?:\.\d+)?)/.exec(selectedModifier || '');
    const modifierPrice = modifierPriceMatch ? Number(modifierPriceMatch[1]) : 0;
    setCartItems([
      {
        id: `${selectedProduct.id}-${selectedModifier}`,
        name: selectedProduct.name,
        price: selectedProduct.price,
        quantity,
        modifier: selectedModifier,
        modifierPrice,
        image: selectedProduct.image,
        storeName: selectedStore?.name || 'Tienda'
      }
    ]);
    setPhase('cart');
  };

  const goToSuccess = () => {
    setPhase('success');
  };

  const renderBrowse = () => (
    <motion.div key="browse" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="tutorial-market-card">
      <div className="tutorial-market-header-row">
        <div>
          <div className="tutorial-market-badge">Simulación guiada</div>
          <h2 className="tutorial-market-title">Comprar en el Mercado</h2>
          <p className="tutorial-market-subtitle">Busca una tienda, revisa productos y arma tu pedido como si estuvieras comprando de verdad.</p>
        </div>
        <button type="button" onClick={() => typeof onComplete === 'function' && onComplete()} className="tutorial-market-icon-btn">
          <X size={20} />
        </button>
      </div>

      <div className="tutorial-market-searchbox">
        <Search size={18} className="text-cyan-300" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ej: hamburguesa, pizza, víveres..."
          className="tutorial-market-input"
        />
      </div>

      <div className="tutorial-market-store-grid">
        {visibleStores.map((store) => (
          <button key={store.id} type="button" onClick={() => openProduct(store, store.products[0])} className="tutorial-market-store-card">
            <div className={`tutorial-market-store-glow bg-gradient-to-r ${store.accent}`} />
            <div className="tutorial-market-store-top">
              <div className="tutorial-market-store-icon"><Store size={18} /></div>
              <span className="tutorial-market-chip">{store.eta}</span>
            </div>
            <h3>{store.name}</h3>
            <p>{store.category}</p>
            <div className="tutorial-market-product-preview">
              <span>{store.products[0].image}</span>
              <div>
                <strong>{store.products[0].name}</strong>
                <small>Desde {formatUsd(store.products[0].price)}</small>
              </div>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );

  const renderProduct = () => (
    <motion.div key="product" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="tutorial-market-card">
      <div className="tutorial-market-header-row">
        <button type="button" onClick={() => setPhase('browse')} className="tutorial-market-back-btn">
          <ArrowLeft size={18} /> Volver
        </button>
        <button type="button" onClick={() => typeof onComplete === 'function' && onComplete()} className="tutorial-market-icon-btn">
          <X size={20} />
        </button>
      </div>

      <div className="tutorial-market-product-hero">
        <div className="tutorial-market-emoji">{selectedProduct?.image}</div>
        <div>
          <div className="tutorial-market-badge">{selectedStore?.name}</div>
          <h2 className="tutorial-market-title">{selectedProduct?.name}</h2>
          <p className="tutorial-market-subtitle">{selectedProduct?.description}</p>
          <div className="tutorial-market-price">{formatUsd(selectedProduct?.price)}</div>
        </div>
      </div>

      <div className="tutorial-market-section">
        <label>Personaliza tu pedido</label>
        <div className="tutorial-market-modifiers">
          {(selectedProduct?.modifiers || []).map((modifier) => (
            <button
              key={modifier}
              type="button"
              onClick={() => setSelectedModifier(modifier)}
              className={`tutorial-market-modifier ${selectedModifier === modifier ? 'active' : ''}`}
            >
              {modifier}
            </button>
          ))}
        </div>
      </div>

      <div className="tutorial-market-qty-row">
        <label>Cantidad</label>
        <div className="tutorial-market-qty-box">
          <button type="button" onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}>-</button>
          <span>{quantity}</span>
          <button type="button" onClick={() => setQuantity((prev) => prev + 1)}>+</button>
        </div>
      </div>

      <button type="button" onClick={addCurrentProductToCart} className="tutorial-market-primary-btn">
        <ShoppingCart size={18} /> Agregar al carrito
      </button>
    </motion.div>
  );

  const renderCart = () => (
    <motion.div key="cart" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="tutorial-market-card">
      <div className="tutorial-market-header-row">
        <button type="button" onClick={() => setPhase('product')} className="tutorial-market-back-btn">
          <ArrowLeft size={18} /> Producto
        </button>
        <div className="tutorial-market-badge"><ShoppingBag size={14} /> Carrito listo</div>
      </div>

      <div className="tutorial-market-cart-list">
        {cartItems.map((item) => (
          <div key={item.id} className="tutorial-market-cart-item">
            <div className="tutorial-market-cart-emoji">{item.image}</div>
            <div className="tutorial-market-cart-info">
              <strong>{item.name}</strong>
              <small>{item.modifier}</small>
              <span>{item.quantity} x {formatUsd(item.price)}</span>
            </div>
            <div className="tutorial-market-cart-total">{formatUsd((item.price + item.modifierPrice) * item.quantity)}</div>
          </div>
        ))}
      </div>

      <div className="tutorial-market-summary">
        <div><span>Subtotal</span><strong>{formatUsd(subtotal)}</strong></div>
        <div><span>Delivery estimado</span><strong>{formatUsd(deliveryFee)}</strong></div>
        <div className="total"><span>Total</span><strong>{formatUsd(total)}</strong></div>
      </div>

      <button type="button" onClick={() => setPhase('delivery')} className="tutorial-market-primary-btn">
        <Truck size={18} /> Continuar con delivery
      </button>
    </motion.div>
  );

  const renderDelivery = () => (
    <motion.div key="delivery" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="tutorial-market-card">
      <div className="tutorial-market-header-row">
        <button type="button" onClick={() => setPhase('cart')} className="tutorial-market-back-btn">
          <ArrowLeft size={18} /> Carrito
        </button>
        <div className="tutorial-market-badge"><MapPin size={14} /> Entrega</div>
      </div>

      <div className="tutorial-market-section">
        <label>Dirección de entrega</label>
        <input className="tutorial-market-input tutorial-market-input-block" value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
      </div>

      <div className="tutorial-market-section">
        <label>Referencia para el repartidor</label>
        <input className="tutorial-market-input tutorial-market-input-block" value={deliveryReference} onChange={(e) => setDeliveryReference(e.target.value)} />
      </div>

      <div className="tutorial-market-delivery-quote">
        <div>
          <strong>Delivery confirmado</strong>
          <p>{selectedStore?.eta} · seguimiento en tiempo real</p>
        </div>
        <div className="tutorial-market-quote-pill">{formatUsd(deliveryFee)}</div>
      </div>

      <button type="button" onClick={() => setPhase('payment')} className="tutorial-market-primary-btn">
        <CreditCard size={18} /> Ir al pago
      </button>
    </motion.div>
  );

  const renderPayment = () => (
    <motion.div key="payment" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="tutorial-market-card">
      <div className="tutorial-market-header-row">
        <button type="button" onClick={() => setPhase('delivery')} className="tutorial-market-back-btn">
          <ArrowLeft size={18} /> Delivery
        </button>
        <div className="tutorial-market-badge"><ShieldCheck size={14} /> Pago seguro</div>
      </div>

      <div className="tutorial-market-payment-list">
        {PAYMENT_METHODS.map((method) => (
          <button
            key={method.id}
            type="button"
            onClick={() => setPaymentMethod(method.id)}
            className={`tutorial-market-payment-option ${paymentMethod === method.id ? 'active' : ''}`}
          >
            <span className="tutorial-market-payment-icon">{method.icon}</span>
            <div>
              <strong>{method.label}</strong>
              <small>{method.note}</small>
            </div>
            {paymentMethod === method.id && <Check size={18} className="text-emerald-300" />}
          </button>
        ))}
      </div>

      <div className="tutorial-market-summary compact">
        <div><span>Pedido</span><strong>{formatUsd(subtotal)}</strong></div>
        <div><span>Delivery</span><strong>{formatUsd(deliveryFee)}</strong></div>
        <div className="total"><span>Total a pagar</span><strong>{paymentMethod === 'fires' ? `${total.toFixed(2)} 🔥` : formatUsd(total)}</strong></div>
      </div>

      <button type="button" onClick={goToSuccess} className="tutorial-market-primary-btn">
        <Sparkles size={18} /> Confirmar pedido
      </button>
    </motion.div>
  );

  const renderSuccess = () => (
    <motion.div key="success" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="tutorial-market-card tutorial-market-success">
      <div className="tutorial-market-success-icon"><Check size={34} /></div>
      <div className="tutorial-market-badge success">Pedido generado</div>
      <h2 className="tutorial-market-title">¡Compra completada!</h2>
      <p className="tutorial-market-subtitle">Tu orden <strong>#MX-4821</strong> quedó en preparación y el delivery ya fue solicitado.</p>

      <div className="tutorial-market-status-list">
        <div><Clock3 size={16} /><span>Pago confirmado con {PAYMENT_METHODS.find((item) => item.id === paymentMethod)?.label}</span></div>
        <div><ShoppingBag size={16} /><span>Pedido enviado a {selectedStore?.name}</span></div>
        <div><Truck size={16} /><span>Repartidor asignado y ruta en camino</span></div>
      </div>

      <p className="tutorial-market-success-note">Cerrando simulación para continuar con tu tutorial...</p>
    </motion.div>
  );

  return (
    <div className="tutorial-simulated-market">
      <AnimatePresence mode="wait">
        {phase === 'browse' && renderBrowse()}
        {phase === 'product' && renderProduct()}
        {phase === 'cart' && renderCart()}
        {phase === 'delivery' && renderDelivery()}
        {phase === 'payment' && renderPayment()}
        {phase === 'success' && renderSuccess()}
      </AnimatePresence>
    </div>
  );
};

export default SimulatedMarketPurchaseFlow;
